import { useCallback } from "react"

/**
 * Hook personalizado para gerenciar o scroll automático e o contador de novas mensagens.
 *
 * @param {React.RefObject<HTMLDivElement>} messagesEndRef - Referência para o elemento final da lista de mensagens.
 * @param {React.RefObject<HTMLDivElement>} messagesListRef - Referência para o container que contém as mensagens.
 * @param {React.MutableRefObject<ReturnType<typeof setTimeout> | null>} scrollTimeoutRef - Referência para o timeout de scroll pendente.
 * @param {React.Dispatch<React.SetStateAction<number>>} setNewMessages - Setter do contador de novas mensagens.
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setShowScrollButton - Setter para visibilidade do botão de scroll.
 * @returns {{ scrollToBottom: (behavior?: ScrollBehavior) => void, lValidateNewMessage: (data: { message: { fromMe?: boolean }}) => void }}
 */
export default function useScrollToBottom(
  messagesEndRef,
  messagesListRef,
  scrollTimeoutRef,
  setNewMessages,
  setShowScrollButton,
) {
  /**
   * Faz o scroll até o final da lista de mensagens.
   *
   * @param {ScrollBehavior} [behavior="auto"] - Define o comportamento da rolagem: `"auto"` ou `"smooth"`.
   */
  const scrollToBottom = useCallback(
    (behavior = "auto") => {
      if (!messagesEndRef.current || !messagesListRef.current) return

      // Cancela qualquer timeout anterior para evitar conflito
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      // Usa timeout para garantir que o DOM esteja atualizado
      scrollTimeoutRef.current = setTimeout(() => {
        // Verifica novamente se as refs ainda existem após o timeout
        if (!messagesListRef.current || !messagesEndRef.current) return

        try {
          messagesListRef.current.scrollTo({
            top: messagesListRef.current.scrollHeight,
            behavior,
          })
        } catch (err) {
          console.error("Erro ao fazer scroll:", err)
          // Fallback para método tradicional
          messagesEndRef.current?.scrollIntoView({ behavior })
        }
      }, 50)

      setNewMessages(0)
      setShowScrollButton(false)
    },
    [messagesEndRef, messagesListRef, scrollTimeoutRef, setNewMessages, setShowScrollButton],
  )

  const lValidateNewMessage = useCallback(
    /**
     * Valida se deve mostrar contador de novas mensagens ou rolar automaticamente.
     *
     * @param {{ message: { fromMe?: boolean }}} data - Dados da nova mensagem recebida.
     */
    (data) => {
      const list = messagesListRef.current
      const shouldShowNewMessages = list ? list.scrollHeight - (list.scrollTop + list.clientHeight) > 200 : false

      if (shouldShowNewMessages && !data.message.fromMe) {
        setNewMessages((prev) => prev + 1)
      } else if (!shouldShowNewMessages) {
        scrollToBottom("smooth")
      }
    },
    [messagesListRef, scrollToBottom, setNewMessages],
  )

  return { scrollToBottom, lValidateNewMessage }
}
